// app/src/lib/delivery/report.js — aggregate delivery statements for reporting.
//
// Pure: takes an array of canonical statement records (from statements.js) and
// rolls them up per-platform and as a grand total, with effective take rates.
// The UI filters the input to a month/range before calling these.

import { BUCKET_KEYS, PLATFORM_IDS, takeCents, takeRate } from './model.js';

const zeroBuckets = () => Object.fromEntries(BUCKET_KEYS.map((k) => [k, 0]));

/** Sum a list of statements into one bucket set (+ derived take). */
function sumBuckets(statements) {
  const b = zeroBuckets();
  for (const s of statements) for (const k of BUCKET_KEYS) b[k] += s[k] || 0;
  return { ...b, takeCents: takeCents(b), takeRate: takeRate(b), count: statements.length };
}

/**
 * Roll statements up per platform + grand total.
 * @param {Object[]} statements - canonical records (already filtered to the period)
 * @returns {{ byPlatform: Object[], total: Object, periodMonths: string[] }}
 */
export function aggregate(statements = []) {
  const byPlatform = [];
  for (const id of PLATFORM_IDS) {
    const forPlatform = statements.filter((s) => s.platform === id);
    if (forPlatform.length === 0) continue;
    byPlatform.push({ platform: id, ...sumBuckets(forPlatform) });
  }
  // Surface any statements whose platform didn't normalize (so they're not lost).
  const unknown = statements.filter((s) => !PLATFORM_IDS.includes(s.platform));
  if (unknown.length) byPlatform.push({ platform: null, ...sumBuckets(unknown) });

  // Sort biggest gross first — that's the platform the operator cares about.
  byPlatform.sort((a, b) => b.grossSalesCents - a.grossSalesCents);

  const periodMonths = [...new Set(statements.map((s) => s.periodMonth).filter(Boolean))].sort();
  return { byPlatform, total: sumBuckets(statements), periodMonths };
}

/** Total delivery gross sales (cents) across the given statements. */
export function totalGrossCents(statements = []) {
  return statements.reduce((a, s) => a + (s.grossSalesCents || 0), 0);
}

/** Total delivery net payout (cents) across the given statements. */
export function totalNetPayoutCents(statements = []) {
  return statements.reduce((a, s) => a + (s.netPayoutCents || 0), 0);
}
